import { useRef, useState, type ComponentPropsWithRef, type MouseEvent, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { clsx } from '../clsx';
import type { Placement } from '../hooks';
import { Icon } from './Icon';
import { Popover } from './Popover';
import styles from './Button.module.css';

export type ButtonVariant =
  'primary' | 'secondary' | 'ghost' | 'ghostInverse' | 'destructive' | 'destructiveFilled' | 'dark' | 'split';
export type ButtonSize = 'small' | 'default' | 'large';

export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'popover'> {
  /**
   * primary: filled accent · secondary: outline · ghost: hairline outline, muted text · ghostInverse: on navy ·
   * destructive: outline in `--danger-fg` · destructiveFilled: filled `--danger-fg` ("DNC setzen") · dark: `--navy-900` ·
   * split: primary with a chevron part that opens `popover` ("Senden ▾ · Später senden").
   */
  variant?: ButtonVariant | undefined;
  /** small 6/11 · 12 px, default 10/18 · 13 px, large 12/18 · 14 px. */
  size?: ButtonSize | undefined;
  block?: boolean | undefined;
  icon?: ReactNode | undefined;
  iconEnd?: ReactNode | undefined;
  /** Shows the in-flight ring, sets `aria-busy` and ignores clicks; keeps the label and width. */
  loading?: boolean | undefined;
  /** split only: content of the popover under the chevron part. */
  popover?: ReactNode | undefined;
  /** split only: controlled open state of the popover (uncontrolled when omitted). */
  popoverOpen?: boolean | undefined;
  onPopoverOpenChange?: ((open: boolean) => void) | undefined;
  popoverPlacement?: Placement | undefined;
  popoverWidth?: number | string | undefined;
  /** split only: accessible name of the chevron part. */
  chevronLabel?: string | undefined;
}

export function Button({
  variant = 'primary',
  size = 'default',
  block = false,
  icon,
  iconEnd,
  loading = false,
  popover,
  popoverOpen,
  onPopoverOpenChange,
  popoverPlacement = 'bottom-end',
  popoverWidth = 262,
  chevronLabel = 'Weitere Optionen',
  className,
  children,
  type = 'button',
  onClick,
  disabled,
  ...rest
}: ButtonProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = popoverOpen ?? innerOpen;

  function setOpen(next: boolean) {
    if (popoverOpen === undefined) setInnerOpen(next);
    onPopoverOpenChange?.(next);
  }

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  }

  const sizeClass = size === 'large' ? styles.large : size === 'small' ? styles.small : undefined;
  const content = (
    <>
      {loading ? <span aria-hidden="true" className={styles.spinner} /> : icon}
      {children}
      {iconEnd}
    </>
  );

  if (variant === 'split') {
    return (
      <span ref={wrapRef} className={clsx(styles.split, block && styles.block)}>
        <button
          type={type}
          className={clsx('pressable', styles.button, styles.primary, styles.splitMain, sizeClass, className)}
          aria-busy={loading || undefined}
          aria-disabled={loading || undefined}
          disabled={disabled}
          onClick={handleClick}
          {...rest}
        >
          {content}
        </button>
        <button
          type="button"
          aria-label={chevronLabel}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          disabled={disabled}
          className={clsx(
            'pressable',
            styles.button,
            styles.primary,
            styles.splitChevron,
            sizeClass,
            isOpen && styles.splitOpen,
          )}
          onClick={() => setOpen(!isOpen)}
        >
          <Icon icon={ChevronDown} size={16} />
        </button>
        {popover ? (
          <Popover
            open={isOpen}
            onClose={() => setOpen(false)}
            anchorRef={wrapRef}
            placement={popoverPlacement}
            width={popoverWidth}
            variant="panel"
            initialFocus="first"
            label={chevronLabel}
          >
            {popover}
          </Popover>
        ) : null}
      </span>
    );
  }

  return (
    <button
      type={type}
      className={clsx(
        'pressable',
        styles.button,
        styles[variant],
        sizeClass,
        block && styles.block,
        className,
      )}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      disabled={disabled}
      onClick={handleClick}
      {...rest}
    >
      {content}
    </button>
  );
}
